import { useEffect, useState } from "react";
import { Bot, CreditCard, Database, Globe, LoaderCircle, Mail, Settings, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={testid}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-right text-xs font-semibold text-slate-700">{value}</span>
    </div>
  );
}

function Card({ icon: Icon, title, children, testid }) {
  return (
    <section className="card-integra p-5" data-testid={testid}>
      <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <Icon className="h-4 w-4 text-bleu" /> {title}
      </h2>
      <div className="mt-2 divide-y divide-slate-50">{children}</div>
    </section>
  );
}

export default function AdminSettings() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les paramètres."));
  }, []);

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="page-admin-settings">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Settings className="h-7 w-7 text-bleu" /> Paramètres
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Configuration de la plateforme (lecture seule). Les clés secrètes Stripe et IA ne sont jamais affichées ni exposées par l'API.
        </p>
      </div>

      <Card icon={CreditCard} title="Paiements Stripe" testid="admin-settings-stripe">
        <Row label="Mode" testid="admin-settings-stripe-mode" value={
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${data.stripe.mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {data.stripe.mode === "live" ? "LIVE (production)" : "TEST"}
          </span>} />
        <Row label="Webhook configuré" value={data.stripe.webhook_configured ? "Oui" : "Non"} />
      </Card>

      <Card icon={Bot} title="Intelligence artificielle" testid="admin-settings-ai">
        <Row label="Fournisseur" value={data.ai.provider} />
        <Row label="Clé configurée" value={data.ai.key_configured ? "Oui" : "Non"} />
      </Card>

      <Card icon={Mail} title="Emails" testid="admin-settings-email">
        <Row label="Expéditeur" value={data.email.from_name} />
        <Row label="Contact" value={data.email.contact} />
      </Card>

      <Card icon={Globe} title="URLs" testid="admin-settings-urls">
        <Row label="Site" value={data.urls.frontend} />
      </Card>

      <Card icon={Database} title="Base de données" testid="admin-settings-db">
        <Row label="Base MongoDB" value={data.database.name} />
      </Card>

      <p className="flex items-center gap-2 rounded-xl bg-bleu-light/60 px-4 py-3 text-[11px] font-semibold text-bleu" data-testid="admin-settings-note">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        Les clés API (Stripe, IA, emails) sont gérées via les variables d'environnement et ne transitent jamais par cette page.
      </p>
    </div>
  );
}
